import { StatusBadge } from '@/components/badge/badge.component';
import { DataTable } from '@/components/ui/data-table';
import { FilterConfig } from '@/components/ui/filter';
import { useTableUrlState } from '@/hooks/useTableUrlState';
import { cn } from '@/lib/utils';
import { Farm, useFarmList } from '@/modules/Farms/Farms.api';
import { usePermissions, UserPermissions } from '@/modules/Permissions';
import { ProjectStatus, projectStatus } from '@/modules/Projects/Projects.constants';
import { ColumnDef, ColumnFiltersState, PaginationState, SortingState } from '@tanstack/react-table';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

const columns: ColumnDef<Farm, unknown>[] = [
  {
    header: 'Farm Name',
    accessorKey: 'farm_name',
    meta: { className: 'w-1/6' },
  },
  {
    header: 'Farm ID',
    accessorKey: 'farm_id',
    meta: { className: 'w-1/6' },
  },
  {
    header: 'Sampling Event',
    accessorKey: 'sampling_event',
    cell: ({ row }) => {
      const samplingEvent = row.original.sampling_event;
      return (
        <div className={cn('flex justify-center')}>
          <span>{samplingEvent !== null && samplingEvent !== undefined ? samplingEvent : '-'}</span>
        </div>
      );
    },
    meta: { className: 'w-1/6' },
  },
  {
    header: 'Farmer Name',
    accessorFn: (farm) => farm.primary_user?.name || '-',
    enableSorting: false, // Sorting on calculated fields not currently supported
    meta: { className: 'w-1/6' },
  },
  {
    header: 'Phone',
    accessorFn: (farm) => farm.primary_user?.phone_number || '-',
    enableSorting: false, // Sorting on calculated fields not currently supported
    meta: { className: 'w-1/6' },
    cell: ({ row }) => {
      const phone = row.original.primary_user?.phone_number || '-';
      return (
        <span title={phone} className="block max-w-[140px] truncate">
          {phone}
        </span>
      );
    },
  },
  {
    header: 'Email',
    accessorFn: (farm) => farm.primary_user?.email || '-',
    enableSorting: false, // Sorting on calculated fields not currently supported
    meta: { className: 'w-1/6' },
    cell: ({ row }) => {
      const email = row.original.primary_user?.email || '-';

      if (email === '-') {
        return <span>{email}</span>;
      }

      return (
        <a
          href={`mailto:${email}`}
          title={email}
          className="block max-w-[140px] truncate text-blue-600 hover:text-blue-800 underline cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {email}
        </a>
      );
    },
  },
  {
    header: 'Reports Released',
    accessorKey: 'status',
    enableSorting: false, // Sorting on calculated fields not currently supported
    cell: ({ row }) => {
      const status = row.getValue<ProjectStatus>('status');
      return (
        <div className={cn('flex justify-center')}>
          <StatusBadge status={status} />
        </div>
      );
    },
    meta: { className: 'w-1/6' },
  },
];

export const FarmsTable = () => {
  const navigate = useNavigate();

  const { pagination, setPagination, sorting, setSorting, columnFilters, setColumnFilters } = useTableUrlState();

  const { projectId } = useParams<{ projectId: string }>();
  const { hasPermission } = usePermissions();
  const { data, status } = useFarmList(pagination, sorting, columnFilters, projectId);

  if (status === 'error') {
    toast.error('Failed to fetch farms');
    return;
  }

  const canAccess = (farm: Farm) => farm.status === projectStatus.COMPLETE || hasPermission(UserPermissions.FARM_DETAIL);

  const handleRowClick = (farm: Farm) => {
    navigate(`/customers/${farm.breadcrumb.client.id}/projects/${farm.breadcrumb.project.id}/farms/${farm.farm_id}/`);
  };

  const filters: FilterConfig[] = [
    {
      id: 'status',
      label: 'Reports Released',
      options: [
        {
          label: 'Incomplete',
          id: 'Incomplete',
        },
        {
          label: 'Complete',
          id: 'Complete',
        },
      ],
    },
  ];

  return (
    <div className={cn('p-5 flex-col gap-3')}>
      <DataTable
        columns={columns}
        data={data?.results}
        totalCount={data?.count}
        pagination={pagination}
        onPaginationChange={setPagination}
        sorting={sorting}
        onSortingChange={setSorting}
        showSearch={true}
        searchPlaceholder="Search Farm Name or ID..."
        filters={filters}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        isLoading={status === 'pending'}
        tableRowClickable={canAccess}
        onTableRowClick={handleRowClick}
      />
    </div>
  );
};
